import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, CartesianGrid, PieChart, Pie, Cell,
} from "recharts";
import {
  FileDown, FileSpreadsheet, TrendingUp, TrendingDown, Wallet, Scale, X, ArrowUp, ArrowDown, Minus, Target, Building2, Flame, Receipt,
} from "lucide-react";
import { formatIDR, periodLabel } from "@/lib/format";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { toast } from "sonner";
import { useAuth, useCabang } from "@/context/AuthContext";

const COLORS = ["#10B981", "#1E3A8A", "#F59E0B", "#F43F5E"];

export default function Dashboard() {
  const { user } = useAuth();
  const { cabangList, selectedCabang, setSelectedCabang } = useCabang();
  const canPickBranch = user?.role !== "cabang";
  const isSuper = user?.role === "super_admin";
  const isViewer = user?.role === "viewer";
  const navigate = useNavigate();

  const [period, setPeriod] = useState("daily");
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [compare, setCompare] = useState(null);
  const [yearly, setYearly] = useState(null);
  const [branches, setBranches] = useState(null);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState("all");
  const dateToRef = useRef(null);

  const handlePeriodChange = (p) => {
    setPeriod(p);
    const now = new Date();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (p === "daily") {
      const t = iso(now); setDateFrom(t); setDateTo(t);
    } else if (p === "weekly") {
      // 7 hari terakhir (termasuk hari ini)
      const start = new Date(now); start.setDate(now.getDate() - 6);
      setDateFrom(iso(start)); setDateTo(iso(now));
    } else if (p === "monthly") {
      // Tanggal 1 bulan berjalan sampai hari ini
      setDateFrom(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
      setDateTo(iso(now));
    } else if (p === "yearly") {
      // 1 Januari sampai hari ini
      setDateFrom(iso(new Date(now.getFullYear(), 0, 1)));
      setDateTo(iso(now));
    }
  };

  // Sinkron tab periode ↔ rentang tanggal manual: bila user ubah Dari/Sampai,
  // deteksi periode yang paling cocok dan sesuaikan pill tab.
  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    const f = new Date(dateFrom + "T00:00:00");
    const t = new Date(dateTo + "T00:00:00");
    if (isNaN(f) || isNaN(t) || f > t) return;
    const dayMs = 86400000;
    const diffDays = Math.round((t - f) / dayMs) + 1;
    let inferred = null;
    if (diffDays === 1) inferred = "daily";
    else if (diffDays === 7) inferred = "weekly";
    else if (f.getDate() === 1 && f.getMonth() === t.getMonth() && f.getFullYear() === t.getFullYear()) inferred = "monthly";
    else if (f.getMonth() === 0 && f.getDate() === 1 && f.getFullYear() === t.getFullYear()) inferred = "yearly";
    if (inferred && inferred !== period) setPeriod(inferred);
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isSuper) return;
    api.get("/users")
      .then(({ data }) => setAdmins(data.filter((u) => u.role === "admin" && (u.workspace_id === user?.active_workspace_id || u.active_workspace_id === user?.active_workspace_id))))
      .catch(() => setAdmins([]));
  }, [isSuper, user?.active_workspace_id]);

  const visibleCabang = useMemo(() => {
    if (!isSuper || selectedAdmin === "all") return cabangList;
    return cabangList.filter((c) => c.admin_id === selectedAdmin);
  }, [cabangList, isSuper, selectedAdmin]);

  useEffect(() => {
    // Reset cabang saat usaha diubah dan cabang lama tidak tersedia lagi
    if (isSuper && selectedAdmin !== "all" && selectedCabang !== "all") {
      if (!visibleCabang.some((c) => c.id === selectedCabang)) setSelectedCabang("all");
    }
  }, [selectedAdmin, visibleCabang, selectedCabang, setSelectedCabang, isSuper]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (dateFrom) params.tanggal_from = dateFrom;
      if (dateTo) params.tanggal_to = dateTo;
      const cabangParam = selectedCabang && selectedCabang !== "all" ? { cabang_id: selectedCabang } : {};
      const adminParam = isSuper && selectedAdmin && selectedAdmin !== "all" ? { admin_id: selectedAdmin } : {};
      const calls = [
        api.get("/dashboard/summary", { params: { ...params, ...cabangParam, ...adminParam } }),
        api.get("/dashboard/compare", { params: { ...cabangParam, ...adminParam } }),
        api.get("/dashboard/yearly", { params: { year, ...cabangParam, ...adminParam } }),
      ];
      if (canPickBranch) calls.push(api.get("/dashboard/branches-compare", { params: adminParam }));
      const results = await Promise.all(calls);
      setData(results[0].data);
      setCompare(results[1].data);
      setYearly(results[2].data);
      if (canPickBranch) setBranches(results[3].data);
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, dateFrom, dateTo, year, selectedCabang, selectedAdmin, canPickBranch, isSuper]);

  useEffect(() => { load(); }, [load]);

  const clearRange = () => { setDateFrom(""); setDateTo(""); };
  const cabangName = (id) => cabangList.find((c) => c.id === id)?.name || id;

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();
    const scope = selectedCabang === "all" ? "Semua Cabang" : cabangName(selectedCabang);
    const t = data.totals;
    // Laba Kotor = margin dari kategori penghasil pendapatan (BB + Insentif)
    // Pengeluaran = Ops + Lainnya (operasional kas keluar)
    // Laba/Rugi = Laba Kotor − Pengeluaran (tanpa double-count)
    const labaKotor = (t.selisih_bahan_baku || 0) + (t.selisih_insentif || 0);
    const pengeluaran = (t.ops || 0) + (t.lainnya || 0);
    const laba = labaKotor - pengeluaran;
    const summary = [
      ["Ringkasan Laporan", `Periode: ${periodLabel(period)}`, `Cabang: ${scope}`],
      dateFrom || dateTo ? [`Rentang: ${dateFrom || "-"} s/d ${dateTo || "-"}`] : [],
      [],
      ["Total Laba Kotor", labaKotor],
      ["  Selisih Bahan Baku", t.selisih_bahan_baku],
      ["  Selisih Insentif Usaha", t.selisih_insentif],
      ["Total Pengeluaran", pengeluaran],
      ["  Operasional (Pembelian)", t.ops],
      ["  Lainnya (Pembelian)", t.lainnya],
      ["Laba/Rugi", laba],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), "Ringkasan");
    XLSX.writeFile(wb, `laporan-usaha-${period}.xlsx`);
    toast.success("Excel diunduh");
  };

  const exportPDF = () => {
    if (!data) return;
    const scope = selectedCabang === "all" ? "Semua Cabang" : cabangName(selectedCabang);
    const t = data.totals;
    const labaKotor = (t.selisih_bahan_baku || 0) + (t.selisih_insentif || 0);
    const pengeluaran = (t.ops || 0) + (t.lainnya || 0);
    const laba = labaKotor - pengeluaran;
    const doc = new jsPDF();
    doc.setFontSize(16); doc.text("Laporan Keuangan Usaha", 14, 18);
    doc.setFontSize(11); doc.text(`Periode: ${periodLabel(period)}`, 14, 26); doc.text(`Cabang: ${scope}`, 14, 32);
    autoTable(doc, {
      startY: 38, head: [["Kategori", "Jumlah"]],
      body: [
        ["Total Laba Kotor", formatIDR(labaKotor)],
        ["  Selisih Bahan Baku", formatIDR(t.selisih_bahan_baku)],
        ["  Selisih Insentif Usaha", formatIDR(t.selisih_insentif)],
        ["Total Pengeluaran", formatIDR(pengeluaran)],
        ["  Operasional (Pembelian)", formatIDR(t.ops)],
        ["  Lainnya (Pembelian)", formatIDR(t.lainnya)],
        ["Laba / Rugi", formatIDR(laba)],
      ],
      headStyles: { fillColor: [15, 23, 42] },
    });
    doc.save(`laporan-usaha-${period}.pdf`);
    toast.success("PDF diunduh");
  };

  const pieData = useMemo(() => {
    if (!data) return [];
    const t = data.totals;
    return [{ name: "Operasional", value: t.ops }, { name: "Lainnya", value: t.lainnya }].filter((d) => d.value > 0);
  }, [data]);

  if (loading || !data) return <div className="text-slate-500">Memuat dashboard...</div>;
  const t = data.totals;
  const scopeLabel = selectedCabang === "all" ? "Semua Cabang" : cabangName(selectedCabang);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard Laporan"
        subtitle={`Ruang lingkup: ${scopeLabel}. Pendapatan = pemasukan manual + selisih (anggaran − pembelian) 4 kategori. Pengeluaran = pembelian Operasional + Lainnya.`}
        actions={
          !isViewer && (
            <>
              <Button data-testid="export-excel" onClick={exportExcel} variant="outline" className="border-slate-300">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
              </Button>
              <Button data-testid="export-pdf" onClick={exportPDF} variant="outline" className="border-slate-300">
                <FileDown className="h-4 w-4 mr-2" /> PDF
              </Button>
            </>
          )
        }
      />

      <Card className="p-4 border-slate-200 flex flex-col lg:flex-row lg:items-end gap-4">
        {isSuper && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Usaha</Label>
            <Select value={selectedAdmin} onValueChange={setSelectedAdmin}>
              <SelectTrigger className="h-9 w-48" data-testid="dashboard-usaha"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Usaha</SelectItem>
                {admins.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {canPickBranch && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Lokasi Cabang</Label>
            <Select value={selectedCabang} onValueChange={setSelectedCabang}>
              <SelectTrigger className="h-9 w-48" data-testid="dashboard-cabang"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">Semua Cabang</SelectItem>
                {visibleCabang.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <Tabs value={period} onValueChange={handlePeriodChange} className="flex-1">
          <div className="mb-1 text-xs text-slate-500 font-semibold uppercase tracking-wide">Periode Agregasi</div>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="daily" data-testid="tab-daily">Harian</TabsTrigger>
            <TabsTrigger value="weekly" data-testid="tab-weekly">Mingguan</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="tab-monthly">Bulanan</TabsTrigger>
            <TabsTrigger value="yearly" data-testid="tab-yearly">Tahunan</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Dari</Label>
            <Input type="date" value={dateFrom}
                   onChange={(e) => {
                     setDateFrom(e.target.value);
                     if (e.target.value) {
                       setTimeout(() => {
                         const el = dateToRef.current;
                         if (!el) return;
                         el.focus();
                         if (typeof el.showPicker === "function") { try { el.showPicker(); } catch { /* ignore */ } }
                       }, 50);
                     }
                   }}
                   className="h-9 w-40" data-testid="date-from" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Sampai</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   ref={dateToRef} className="h-9 w-40" data-testid="date-to" />
          </div>
          {(dateFrom || dateTo) && (
            <Button variant="ghost" size="icon" onClick={clearRange} data-testid="date-clear"><X className="h-4 w-4" /></Button>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Metric icon={Wallet} tone={t.selisih_bahan_baku >= 0 ? "emerald" : "rose"} label="Bahan Baku" value={formatIDR(t.selisih_bahan_baku)} sub="Penjualan − Pembelian" testid="metric-bahan-baku" onClick={() => navigate("/riwayat?tab=bahan_baku")} />
        <Metric icon={Wallet} tone={t.selisih_ops >= 0 ? "emerald" : "rose"} label="Operasional" value={formatIDR(t.selisih_ops)} sub="Anggaran − Pembelian" testid="metric-ops" onClick={() => navigate("/riwayat?tab=ops")} />
        <Metric icon={Wallet} tone={t.selisih_insentif >= 0 ? "emerald" : "rose"} label="Insentif Usaha" value={formatIDR(t.selisih_insentif)} sub="Anggaran − Realisasi" testid="metric-insentif" onClick={() => navigate("/riwayat?tab=insentif")} />
        <Metric icon={Wallet} tone={t.selisih_lainnya >= 0 ? "emerald" : "rose"} label="Lainnya" value={formatIDR(t.selisih_lainnya)} sub="0 − Pembelian" testid="metric-lainnya" onClick={() => navigate("/riwayat?tab=lainnya")} />
        <Metric icon={TrendingUp} tone={t.pendapatan >= 0 ? "emerald" : "rose"} label="Laba Kotor" value={formatIDR(t.pendapatan)} sub="BB + Ops + Insentif + Lainnya" testid="metric-laba-kotor" onClick={() => navigate("/riwayat?tab=semua")} />
      </div>

      <TagihanSummaryCard onClick={() => navigate("/tagihan")} />

      {canPickBranch && branches && branches.branches.length > 0 && <BranchesCompareCard data={branches} />}
      {canPickBranch && branches && branches.branches.length > 0 && <BudgetPerBranchCard data={branches} />}
      {yearly && yearly.months && <TrendChart yearly={yearly} />}
      {(user?.role === "super_admin" || user?.role === "admin") && <TopItemsCard cabangId={selectedCabang} adminId={selectedAdmin} />}
      {(user?.role === "super_admin" || user?.role === "admin") && <AiInsightsCard cabangId={selectedCabang} adminId={selectedAdmin} />}

      {compare && <BudgetProgress compare={compare} />}
      {yearly && <YearlyChart yearly={yearly} year={year} setYear={setYear} />}
      {compare && <CompareCard compare={compare} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-6 border-slate-200">
          <div className="mb-4">
            <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Tren Pendapatan vs Pengeluaran</div>
            <div className="text-xs text-slate-500">Periode {periodLabel(period).toLowerCase()} · {scopeLabel}</div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.timeline}>
                <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => new Intl.NumberFormat("id-ID", { notation: "compact" }).format(v)} />
                <Tooltip formatter={(v) => formatIDR(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="pendapatan" name="Pendapatan" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="pengeluaran" name="Pengeluaran" stroke="#F43F5E" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <div className="mb-4">
            <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Komposisi Pengeluaran</div>
            <div className="text-xs text-slate-500">Operasional + Lainnya</div>
          </div>
          <div className="h-72">
            {pieData.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400">Belum ada pengeluaran</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {pieData.map((d, i) => <Cell key={d.name} fill={COLORS[(i + 1) % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => formatIDR(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function BudgetPerBranchCard({ data }) {
  const CAT_LABEL = { bahan_baku: "Bahan Baku", ops: "Operasional", insentif: "Insentif" };
  return (
    <Card className="p-6 border-slate-200" data-testid="budget-per-branch-card">
      <div className="mb-4 flex items-center gap-2">
        <Flame className="h-4 w-4 text-rose-600" />
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Anggaran vs Realisasi per Cabang</div>
        <span className="ml-auto text-xs text-slate-500">Periode {data.month}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {data.branches.map((b) => {
          const cats = ["bahan_baku", "ops", "insentif"];
          const worst = b.worst_category;
          const worstPct = Number(b.worst_pct || 0);
          const tone = worstPct >= 100 ? "border-rose-300 bg-rose-50"
                     : worstPct >= 80 ? "border-amber-300 bg-amber-50"
                     : "border-emerald-200 bg-emerald-50/50";
          const badge = worstPct >= 100 ? "bg-rose-600 text-white"
                       : worstPct >= 80 ? "bg-amber-500 text-white"
                       : "bg-emerald-600 text-white";
          return (
            <div key={b.cabang_id} className={`rounded-xl border-2 p-4 ${tone}`} data-testid={`budget-branch-${b.cabang_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="font-semibold text-slate-900">{b.name}</div>
                {worst ? (
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 ${badge}`}>
                    <Flame className="h-3 w-3" /> {CAT_LABEL[worst]} {worstPct.toFixed(0)}%
                  </span>
                ) : (
                  <span className="inline-flex text-[10px] font-bold uppercase rounded-full px-2 py-1 bg-slate-200 text-slate-600">Belum ada anggaran</span>
                )}
              </div>
              <div className="space-y-2.5">
                {cats.map((k) => {
                  const c = b.categories[k];
                  const pct = Number(c.pct || 0);
                  const clamped = Math.min(pct, 100);
                  const isWorst = k === worst;
                  const barTone = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
                  return (
                    <div key={k}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className={`${isWorst ? "font-bold text-slate-900" : "text-slate-600"}`}>{CAT_LABEL[k]}</span>
                        <span className="text-slate-500">{formatIDR(c.realisasi)} / {formatIDR(c.anggaran)}</span>
                      </div>
                      <div className="h-2 rounded-full bg-white/70 overflow-hidden">
                        <div className={`h-full ${barTone} transition-all`} style={{ width: `${clamped}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function BranchesCompareCard({ data }) {
  return (
    <Card className="p-6 border-slate-200" data-testid="branches-compare-card">
      <div className="mb-4 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-emerald-600" />
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Bandingkan Cabang</div>
        <span className="ml-auto text-xs text-slate-500">Periode {data.month}</span>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data.branches}>
            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => new Intl.NumberFormat("id-ID", { notation: "compact" }).format(v)} />
            <Tooltip formatter={(v) => formatIDR(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="pendapatan" name="Pendapatan" fill="#10B981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#F43F5E" radius={[4, 4, 0, 0]} />
            <Bar dataKey="laba" name="Laba" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.branches.map((b) => (
          <div key={b.cabang_id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`branch-tile-${b.cabang_id}`}>
            <div className="text-sm font-semibold text-slate-900">{b.name}</div>
            <div className="mt-1 text-xs text-slate-500">Pendapatan <b className="text-emerald-600">{formatIDR(b.pendapatan)}</b></div>
            <div className="text-xs text-slate-500">Pengeluaran <b className="text-rose-600">{formatIDR(b.pengeluaran)}</b></div>
            <div className="text-xs text-slate-500">Laba <b className={b.laba >= 0 ? "text-emerald-600" : "text-rose-600"}>{formatIDR(b.laba)}</b></div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function YearlyChart({ yearly, year, setYear }) {
  const years = []; const nowY = new Date().getFullYear();
  for (let y = nowY - 4; y <= nowY + 1; y++) years.push(y);
  const totals = yearly.totals || {};
  return (
    <Card className="p-6 border-slate-200" data-testid="yearly-card">
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Grafik Tahunan</div>
          <div className="text-xs text-slate-500">Pendapatan vs Pengeluaran tiap bulan sepanjang tahun {yearly.year}</div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-xs text-slate-500">
            <span className="mr-3">Pendapatan <b className="text-emerald-600">{formatIDR(totals.pendapatan)}</b></span>
            <span className="mr-3">Pengeluaran <b className="text-rose-600">{formatIDR(totals.pengeluaran)}</b></span>
            <span>Laba <b className={totals.laba >= 0 ? "text-emerald-600" : "text-rose-600"}>{formatIDR(totals.laba)}</b></span>
          </div>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
                  className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="yearly-year-select">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={yearly.months} barGap={4}>
            <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
            <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => new Intl.NumberFormat("id-ID", { notation: "compact" }).format(v)} />
            <Tooltip formatter={(v) => formatIDR(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="pendapatan" name="Pendapatan" fill="#10B981" radius={[4, 4, 0, 0]} />
            <Bar dataKey="pengeluaran" name="Pengeluaran" fill="#F43F5E" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}

function BudgetProgress({ compare }) {
  const cats = [
    { key: "bahan_baku", label: "Bahan Baku", anggaranKey: "anggaran_bahan_baku" },
    { key: "ops", label: "Operasional", anggaranKey: "anggaran_ops" },
    { key: "insentif", label: "Insentif Usaha", anggaranKey: "anggaran_insentif" },
  ];
  return (
    <Card className="p-6 border-slate-200" data-testid="budget-progress-card">
      <div className="mb-4 flex items-center gap-2">
        <Target className="h-4 w-4 text-emerald-600" />
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Grafik Anggaran Bulan Ini</div>
        <span className="ml-auto text-xs text-slate-500">{compare.current_month}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cats.map((c) => {
          const anggaran = Number(compare.current[c.anggaranKey] || 0);
          const realisasi = Number(compare.current[c.key] || 0);
          const pct = anggaran > 0 ? (realisasi / anggaran) * 100 : 0;
          const clamped = Math.min(pct, 100);
          const tone = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
          const badge = pct >= 100 ? "text-rose-700 bg-rose-50 border-rose-200"
                     : pct >= 80 ? "text-amber-700 bg-amber-50 border-amber-200"
                     : "text-emerald-700 bg-emerald-50 border-emerald-200";
          return (
            <div key={c.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`budget-${c.key}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-900">{c.label}</div>
                <span className={`inline-flex text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-1 border ${badge}`}>
                  {pct.toFixed(0)}%
                </span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className={`h-full ${tone} transition-all`} style={{ width: `${clamped}%` }} />
              </div>
              <div className="mt-2 flex items-baseline justify-between text-xs text-slate-500">
                <span>Realisasi <span className="text-slate-900 font-semibold">{formatIDR(realisasi)}</span></span>
                <span>Anggaran <span className="text-slate-900 font-semibold">{formatIDR(anggaran)}</span></span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function CompareCard({ compare }) {
  const rows = [
    { key: "pendapatan", label: "Pendapatan", positiveIsGood: true },
    { key: "pengeluaran", label: "Pengeluaran", positiveIsGood: false },
    { key: "laba", label: "Laba / Rugi", positiveIsGood: true },
    { key: "selisih_bahan_baku", label: "Selisih Bahan Baku", positiveIsGood: true },
    { key: "selisih_ops", label: "Selisih Operasional", positiveIsGood: true },
    { key: "selisih_insentif", label: "Selisih Insentif", positiveIsGood: true },
    { key: "lainnya", label: "Lainnya (Out)", positiveIsGood: false },
  ];
  return (
    <Card className="p-6 border-slate-200" data-testid="compare-card">
      <div className="mb-4">
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Bandingkan Periode</div>
        <div className="text-xs text-slate-500">{compare.current_month} vs {compare.previous_month}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        {rows.map((r) => {
          const cur = Number(compare.current[r.key] || 0);
          const prev = Number(compare.previous[r.key] || 0);
          const delta = cur - prev;
          const pct = prev !== 0 ? (delta / Math.abs(prev)) * 100 : (cur !== 0 ? 100 : 0);
          const up = delta > 0; const down = delta < 0;
          const good = (up && r.positiveIsGood) || (down && !r.positiveIsGood);
          const bad = (down && r.positiveIsGood) || (up && !r.positiveIsGood);
          const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
          const tone = good ? "text-emerald-600 bg-emerald-50" : bad ? "text-rose-600 bg-rose-50" : "text-slate-500 bg-slate-100";
          return (
            <div key={r.key} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`compare-${r.key}`}>
              <div className="text-xs text-slate-500 font-semibold uppercase tracking-wide">{r.label}</div>
              <div className="mt-2 font-heading text-lg font-bold tracking-tight text-slate-900">{formatIDR(cur)}</div>
              <div className="text-[11px] text-slate-400 mt-0.5">Sebelumnya {formatIDR(prev)}</div>
              <div className={`mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone}`}>
                <Icon className="h-3 w-3" />
                {isFinite(pct) ? `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%` : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function TopItemsCard({ cabangId, adminId }) {
  const [items, setItems] = useState([]);
  const [kategori, setKategori] = useState("bahan_baku");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = { kategori };
        const today = new Date();
        params.tanggal_from = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
        params.tanggal_to = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()).padStart(2, "0")}`;
        if (cabangId && cabangId !== "all") params.cabang_id = cabangId;
        if (adminId && adminId !== "all") params.admin_id = adminId;
        const { data } = await api.get("/analytics/items", { params });
        setItems(data.items || []);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    };
    load();
  }, [kategori, cabangId, adminId]);

  return (
    <Card className="p-6 border-slate-200" data-testid="top-items-card">
      <div className="mb-4 flex items-center gap-3">
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Top Item Bulan Ini</div>
        <Select value={kategori} onValueChange={setKategori}>
          <SelectTrigger className="h-8 w-40 ml-auto" data-testid="top-items-kategori"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="bahan_baku">Bahan Baku</SelectItem>
            <SelectItem value="ops">Operasional</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="py-8 text-center text-sm text-slate-500">Menganalisis...</div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400">Belum ada data bulan ini</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase text-slate-500 border-b border-slate-200">
                <th className="text-left py-2 font-semibold">Item</th>
                <th className="text-right py-2 font-semibold">Jumlah</th>
                <th className="text-right py-2 font-semibold">Frekuensi</th>
                <th className="text-right py-2 font-semibold">Total Pembelian</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 font-medium">{it.item}</td>
                  <td className="text-right text-slate-700">{it.total_jumlah > 0 ? it.total_jumlah.toFixed(1) : "—"}</td>
                  <td className="text-right text-slate-500">{it.frekuensi}×</td>
                  <td className="text-right font-mono font-semibold text-slate-900">{formatIDR(it.total_pembelian)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function AiInsightsCard({ cabangId, adminId }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generate = async () => {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (cabangId && cabangId !== "all") params.cabang_id = cabangId;
      if (adminId && adminId !== "all") params.admin_id = adminId;
      const { data } = await api.get("/dashboard/ai-insights", { params });
      setInsight(data.insight);
    } catch (err) {
      setError(err?.response?.data?.detail || "Gagal generate insight");
    } finally { setLoading(false); }
  };

  return (
    <Card className="p-6 border-slate-200 bg-gradient-to-br from-emerald-50 via-white to-blue-50" data-testid="ai-insights-card">
      <div className="mb-3 flex items-center gap-2">
        <div className="h-7 w-7 rounded-lg bg-emerald-600 text-white flex items-center justify-center">✨</div>
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">AI Insight Bulanan</div>
        <span className="ml-auto text-xs text-slate-500">Powered by Gemini</span>
      </div>
      {insight ? (
        <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-testid="ai-insight-text">{insight}</div>
      ) : error ? (
        <div className="text-sm text-rose-600">{error}</div>
      ) : (
        <div className="text-sm text-slate-500">Klik tombol di bawah untuk generate ringkasan analitis bulan ini vs bulan lalu.</div>
      )}
      <div className="mt-4 flex gap-2">
        <Button onClick={generate} disabled={loading} size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="generate-insight-btn">
          {loading ? "Menganalisis..." : insight ? "Refresh Insight" : "Generate Insight"}
        </Button>
      </div>
    </Card>
  );
}

function TrendChart({ yearly }) {
  // Ambil 6 bulan terakhir dari data yearly
  const months = (yearly.months || []).slice(-6);
  const data = months.map((m) => ({
    label: m.label.slice(0, 3),
    "Laba Kotor": Math.round(m.pendapatan),
    Pengeluaran: Math.round(m.pengeluaran),
    "Laba/Rugi": Math.round(m.laba),
  }));
  const empty = data.every((d) => d["Laba Kotor"] === 0 && d.Pengeluaran === 0 && d["Laba/Rugi"] === 0);
  return (
    <Card className="p-6 border-slate-200" data-testid="trend-chart-card">
      <div className="mb-4 flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-emerald-600" />
        <div className="font-heading text-lg font-bold tracking-tight text-slate-900">Tren 6 Bulan Terakhir</div>
        <span className="ml-auto text-xs text-slate-500">Tahun {yearly.year}</span>
      </div>
      {empty ? (
        <div className="py-12 text-center text-sm text-slate-400">Belum cukup data untuk menampilkan tren</div>
      ) : (
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={data} margin={{ top: 10, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(v) => v >= 1000000 ? `${(v / 1000000).toFixed(1)}jt` : v >= 1000 ? `${(v / 1000).toFixed(0)}rb` : v} />
              <Tooltip formatter={(v) => formatIDR(v)} labelStyle={{ color: "#0F172A", fontWeight: 700 }} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="Laba Kotor" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Pengeluaran" stroke="#F43F5E" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Laba/Rugi" stroke="#1E3A8A" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

function TagihanSummaryCard({ onClick }) {
  const [state, setState] = useState({ total: 0, count: 0, loading: true });
  useEffect(() => {
    let alive = true;
    api.get("/tagihan").then(({ data }) => {
      if (alive) setState({ total: data?.total || 0, count: data?.count || 0, loading: false });
    }).catch(() => alive && setState({ total: 0, count: 0, loading: false }));
    return () => { alive = false; };
  }, []);
  if (state.loading || state.count === 0) return null;
  return (
    <Card
      className="p-5 border-rose-200 bg-gradient-to-br from-rose-50 to-white hover:shadow-md transition-all hover:-translate-y-0.5 cursor-pointer"
      data-testid="tagihan-summary-card"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
    >
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-rose-100 border border-rose-200 flex items-center justify-center">
          <Receipt className="h-6 w-6 text-rose-600" />
        </div>
        <div className="flex-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-rose-700">Tagihan Belum Lunas</div>
          <div className="font-heading text-2xl font-extrabold tracking-tight text-rose-700" data-testid="tagihan-summary-total">
            {formatIDR(state.total)} <span className="text-sm font-semibold text-rose-500">· {state.count} item</span>
          </div>
        </div>
        <div className="text-xs text-rose-600 font-semibold">Kelola →</div>
      </div>
    </Card>
  );
}

function Metric({ icon: Icon, tone, label, value, sub, testid, onClick }) {
  const map = {
    emerald: "text-emerald-600 bg-emerald-50 border-emerald-200",
    rose: "text-rose-600 bg-rose-50 border-rose-200",
    blue: "text-blue-700 bg-blue-50 border-blue-200",
  };
  const clickable = typeof onClick === "function";
  return (
    <Card
      className={`p-6 border-slate-200 hover:shadow-md transition-all hover:-translate-y-0.5 ${clickable ? "cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500" : ""}`}
      data-testid={testid}
      onClick={onClick}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm text-slate-500 font-medium">{label}</div>
        </div>
        <div className={`h-9 w-9 rounded-lg border flex items-center justify-center ${map[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">{value}</div>
      {clickable && <div className="text-[10px] text-emerald-600 font-semibold mt-2 inline-flex items-center gap-1">Klik untuk lihat rincian →</div>}
    </Card>
  );
}
